import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useNavigate } from 'react-router-dom';
import {
  Edit, Trash2, Eye, ShoppingCart, Receipt, Truck, AlertTriangle,
  Globe, DollarSign, Percent, Crown, Coins, Building2,
} from 'lucide-react';

/**
 * Unified branch card — used for both "Main Branch" (isMain) and every "Other Branch".
 * Props-based only; does not reach into parent state. UI matches the previous inline markup.
 */
export default function BranchCard({
  branch,
  summary: s,
  isMain = false,
  currencySymbol: cur,
  countryName,
  capital = 0,
  onSetMain,
  onCapital,
  onViewCapitalList,
  onView,
  onEdit,
  onDelete,
  onToggleAutoOffset,
}) {
  const navigate = useNavigate();
  // ---- Style switches based on isMain ----
  const cardClass = isMain
    ? 'border-2 border-warning/30 bg-gradient-to-br from-amber-50/50 to-white shadow-md'
    : 'border-border hover:shadow-lg transition-shadow w-full';

  const titleClass = isMain ? 'text-xl font-bold truncate' : 'block truncate';
  const kpiPad = isMain ? 'p-3' : 'p-2';
  const kpiIcon = isMain ? 12 : 11;
  const kpiValueClass = isMain ? 'text-lg font-bold' : 'text-sm font-bold';
  const kpiGap = isMain ? 'gap-3' : 'gap-2';
  const kpiGrid = isMain
    ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7'
    : 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7';
  const rounding = isMain ? 'rounded-lg' : 'rounded';
  const precision = isMain ? 2 : 0;

  return (
    <Card className={cardClass} data-testid={isMain ? 'main-branch-card' : 'branch-card'}>
      <CardHeader className={isMain ? 'pb-3' : ''}>
        <CardTitle className="font-outfit flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className={titleClass} title={branch.name}>{branch.name}</span>
              {isMain && <Badge className="bg-warning/20 text-warning border-warning/30 text-[10px]">Main</Badge>}
            </div>
            {branch.location && <p className="text-sm font-normal text-muted-foreground mt-1">{branch.location}</p>}
            <div className="flex flex-wrap gap-1 mt-2">
              {countryName && (
                <Badge variant="outline" className="text-[10px]" data-testid="branch-country-badge">
                  <Globe size={10} className="mr-1" />{countryName}
                </Badge>
              )}
              {branch.currency_code && (
                <Badge variant="secondary" className="text-[10px]" data-testid="branch-currency-badge">
                  <DollarSign size={10} className="mr-0.5" />{branch.currency_code}
                </Badge>
              )}
              {branch.tax_rate != null && (
                <Badge variant="outline" className="text-[10px] border-warning/30 bg-warning/10" data-testid="branch-tax-badge">
                  <Percent size={10} className="mr-0.5" />{branch.tax_name || 'Tax'} {branch.tax_rate}%
                </Badge>
              )}
              {(() => {
                const ovKeys = ['company_phone', 'company_address', 'company_email', 'company_vat'];
                const n = ovKeys.filter(k => String(branch[k] || '').trim()).length;
                return n > 0 ? (
                  <Badge variant="outline" className="text-[10px] border-emerald-300 bg-emerald-50 text-emerald-700" data-testid="branch-override-badge" title="This branch uses its own contact details on receipts & invoices (managed in Branding & PDF)">
                    <Building2 size={10} className="mr-0.5" />Own details
                  </Badge>
                ) : null;
              })()}
              {s?.inter_branch && Math.abs(s.inter_branch.net_outstanding || 0) > 0.5 && (
                <span className="inline-flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => navigate(`/inter-branch-ledger?branch=${branch.id}`)}
                    data-testid={`branch-interbranch-chip-${branch.id}`}
                    title={s.inter_branch.net_outstanding >= 0 ? 'Other branches owe this branch — view ledger' : 'This branch owes other branches — view ledger'}
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors ${s.inter_branch.net_outstanding >= 0 ? 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100'}`}
                  >
                    <Coins size={10} className="mr-0.5" />
                    {s.inter_branch.net_outstanding >= 0 ? 'Owed to you ' : 'You owe '}
                    {cur} {Math.abs(s.inter_branch.net_outstanding).toFixed(0)}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const bd = (s.inter_branch.breakdown || []).slice().sort((a, b) => (b.amount || 0) - (a.amount || 0));
                      const top = bd[0];
                      if (top && top.branch_id && top.amount) {
                        // owes_us: other→us (they pay us); we_owe: us→other (we pay them)
                        const [fromId, toId] = top.direction === 'owes_us'
                          ? [top.branch_id, branch.id]
                          : [branch.id, top.branch_id];
                        navigate(`/inter-branch-ledger?branch=${branch.id}&settle=1&from=${fromId}&to=${toId}&amt=${top.amount}`);
                      } else {
                        navigate(`/inter-branch-ledger?branch=${branch.id}&settle=1`);
                      }
                    }}
                    data-testid={`branch-interbranch-settle-${branch.id}`}
                    title="Settle this branch's largest inter-branch balance (pre-filled)"
                    className="inline-flex items-center rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10px] font-medium text-muted-foreground hover:bg-muted"
                  >
                    Settle
                  </button>
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-1 shrink-0">
            {!isMain && onSetMain && (
              <Button size="sm" variant="ghost" onClick={() => onSetMain(branch.id)} title="Set as Main Branch" data-testid="set-main-branch-btn">
                <Crown size={16} className="text-warning" />
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => onCapital(branch)} title="Record Capital / Goodwill" data-testid={`branch-capital-btn-${branch.id}`}>
              <Coins size={16} className="text-success" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onView(s)} data-testid={isMain ? 'main-branch-view-btn' : 'view-branch-btn'}>
              <Eye size={16} />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onEdit(branch)} data-testid={isMain ? 'main-branch-edit-btn' : 'edit-branch-button'}>
              <Edit size={16} />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onDelete(branch.id)} data-testid={isMain ? 'main-branch-delete-btn' : 'delete-branch-button'} className="text-error hover:text-error">
              <Trash2 size={16} />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {s ? (
          <div className={isMain ? undefined : 'space-y-3'}>
            <div className={`${kpiGrid} ${kpiGap}`}>
              <div className={`flex flex-col ${kpiPad} bg-success/10 ${rounding}`}>
                <span className="text-[10px] md:text-xs text-muted-foreground flex items-center gap-1"><ShoppingCart size={kpiIcon} />Sales</span>
                <span className={`${kpiValueClass} text-success mt-0.5 md:mt-1`}>{cur} {s.total_sales.toFixed(precision)}</span>
              </div>
              <div className={`flex flex-col ${kpiPad} bg-error/10 ${rounding}`}>
                <span className="text-[10px] md:text-xs text-muted-foreground flex items-center gap-1"><Receipt size={kpiIcon} />Expenses</span>
                <span className={`${kpiValueClass} text-error mt-0.5 md:mt-1`}>{cur} {s.total_expenses.toFixed(precision)}</span>
              </div>
              <div className={`flex flex-col ${kpiPad} bg-info/10 ${rounding}`}>
                <span className="text-[10px] md:text-xs text-muted-foreground flex items-center gap-1"><Truck size={kpiIcon} />Supplier</span>
                <span className={`${kpiValueClass} text-info mt-0.5 md:mt-1`}>{cur} {s.total_supplier_payments.toFixed(precision)}</span>
              </div>
              <div className={`flex flex-col ${kpiPad} bg-primary/10 ${rounding} border border-primary/20`}>
                <span className="text-[10px] md:text-xs text-muted-foreground font-medium">Net Profit</span>
                <span className={`${kpiValueClass} mt-0.5 md:mt-1 ${s.net_profit >= 0 ? 'text-success' : 'text-error'}`}>{cur} {s.net_profit.toFixed(precision)}</span>
              </div>
              <div className={`flex flex-col ${kpiPad} bg-cash/10 ${rounding}`}>
                <span className="text-[10px] md:text-xs text-muted-foreground">Cash</span>
                <span className={`${kpiValueClass} mt-0.5 md:mt-1 ${(s.cash_in_hand || 0) >= 0 ? 'text-cash' : 'text-error'}`}>{cur} {(s.cash_in_hand || 0).toFixed(0)}</span>
              </div>
              <div className={`flex flex-col ${kpiPad} bg-bank/10 ${rounding}`}>
                <span className="text-[10px] md:text-xs text-muted-foreground">Bank</span>
                <span className={`${kpiValueClass} mt-0.5 md:mt-1 ${(s.bank_in_hand || 0) >= 0 ? 'text-bank' : 'text-error'}`}>{cur} {(s.bank_in_hand || 0).toFixed(0)}</span>
              </div>
              <button
                type="button"
                onClick={() => onViewCapitalList(branch)}
                className={`flex flex-col ${kpiPad} bg-success/10 hover:bg-success/20 transition-colors ${rounding} border border-success/30 text-left`}
                data-testid={`branch-capital-invested-${branch.id}`}
                title="Click to view breakdown"
              >
                <span className="text-[10px] md:text-xs text-success flex items-center gap-1"><Coins size={kpiIcon} />{isMain ? 'Capital Invested' : 'Capital'}</span>
                <span className={`${kpiValueClass} text-success mt-0.5 md:mt-1`}>{cur} {capital.toFixed(precision)}</span>
              </button>
            </div>

            {!isMain && s.expense_categories && Object.keys(s.expense_categories).length > 0 && (
              <div className="pt-2 border-t mt-2">
                <p className="text-xs text-muted-foreground mb-1">Top Expenses:</p>
                <div className="flex gap-1 flex-wrap">
                  {Object.entries(s.expense_categories).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([cat, amt]) => (
                    <Badge key={cat} variant="outline" className="text-xs capitalize">
                      {cat.replace('_', ' ')}: {cur} {amt.toFixed(0)}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {!isMain && s.is_loss && (
              <div className="mt-2 p-2 bg-error/10 rounded border border-error/30 flex items-center gap-2">
                <AlertTriangle size={14} className="text-error" />
                <span className="text-xs font-bold text-error">This branch is running at a LOSS</span>
              </div>
            )}

            {onToggleAutoOffset && (
              <label className="mt-2 flex items-center justify-between gap-2 pt-2 border-t cursor-pointer">
                <span className="text-[11px] text-muted-foreground flex items-center gap-1" title="Automatically net this branch's inter-branch balances in real time + nightly, using the Internal-Offset clearing house (no cash/bank movement)">
                  <Coins size={12} className="text-indigo-500" /> Auto-net internal offsets
                </span>
                <Switch
                  checked={!!s.auto_offset_enabled}
                  onCheckedChange={(v) => onToggleAutoOffset(branch.id, v)}
                  data-testid={`branch-auto-offset-${branch.id}`}
                />
              </label>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Loading stats...</p>
        )}
      </CardContent>
    </Card>
  );
}
